import Flag16 from '@carbon/icons-react/es/flag/16';
import Locked16 from '@carbon/icons-react/es/locked/16';
import Task16 from '@carbon/icons-react/es/task/16';
import Unlocked16 from '@carbon/icons-react/es/unlocked/16';
import { Tooltip } from 'antd';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { DateType, ProjectId } from 'api/models';
import { Project } from 'api/project';
import { StatusFieldOption } from 'api/tasks';
import { Avatar } from 'components/atoms/avatar';
import { Menu } from 'components/atoms/dropdown';
import { AutoAllocate16 } from 'components/atoms/icons/auto-allocate';
import { ShiftDates16 } from 'components/atoms/icons/shift-dates';
import { NameCell } from 'components/organisms/timesheets/common/cells/name-cell';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { getThumbnailUrl } from 'utils/get-thumbnail-url';
import { getProjectPlanUrl } from 'utils/urls';
import {
  CellWrapper,
  ProjectStatusIndicator,
  SecondaryTextWrapper,
  TextWrapperWithEllipsis,
} from './component';
import { useAutoAllocateConfirmation } from './hooks/auto-allocate-confirmation';
import { useAllocationsMoreActions } from './use-allocations-more-options';

interface Props
  extends Pick<
    Project,
    'projectName' | 'projectId' | 'customer' | 'autoAllocation'
  > {
  status: StatusFieldOption;
  startDate?: DateType;
  showShiftStartDate: boolean;
  softAllocationsEnabled: boolean;
  onConvertAllocations: (toSoft: boolean) => void;
  onWrapperClick: () => void;
  onShiftStartDate: () => void;
  goToStartOfProject: () => void;
  enableAutoAllocate: () => void;
  disableAutoAllocate: () => void;
}

export const ProjectCellForProjectsView: FC<Props> = ({
  projectName,
  projectId,
  startDate,
  customer,
  status,
  showShiftStartDate,
  softAllocationsEnabled,
  onConvertAllocations,
  onWrapperClick,
  onShiftStartDate,
  goToStartOfProject,
  autoAllocation: autoAllocate = false,
  enableAutoAllocate,
  disableAutoAllocate,
}) => {
  const openProjectplan = useCallback(() => {
    window.open(getProjectPlanUrl(ProjectId(projectId)), '_blank');
  }, [projectId]);
  const { changeAutoAllocateState } = useAutoAllocateConfirmation({
    onAutoAllocateDisable: disableAutoAllocate,
    onAutoAllocateEnable: enableAutoAllocate,
  });
  const isCollaborator = useIsCurrentUserCollaborator();

  const {
    detailedAllocatedMinutes,
    onMenuVisibleChange,
    showConvertAllocationsConfirmation,
  } = useAllocationsMoreActions({
    resourceName: projectName,
    projectId,
    onConvertAllocations,
  });

  return (
    <CellWrapper
      onMenuVisibleChange={onMenuVisibleChange}
      menu={
        <Menu>
          <Menu.Header>
            <span>
              <FormattedMessage id="more_actions" />
            </span>
          </Menu.Header>
          <Menu.Item icon={<Task16 />} onClick={openProjectplan}>
            <FormattedMessage id="resource_management.actions.go_to_plan" />
          </Menu.Item>
          {showShiftStartDate ? (
            <Menu.Item
              icon={<ShiftDates16 />}
              onClick={onShiftStartDate}
              disabled={startDate == null}
            >
              <Tooltip
                title={
                  startDate == null ? (
                    <FormattedMessage id="resource_management.actions.shift_start_date.disabled" />
                  ) : null
                }
              >
                <span>
                  <FormattedMessage id="resource_management.actions.shift_start_date" />
                </span>
              </Tooltip>
            </Menu.Item>
          ) : null}
          {!isCollaborator ? (
            <Menu.Item
              icon={<AutoAllocate16 />}
              onClick={() => changeAutoAllocateState(autoAllocate)}
            >
              <span>
                {autoAllocate === true ? (
                  <FormattedMessage id="resource_management.actions.auto_allocate.disable.label" />
                ) : (
                  <FormattedMessage id="resource_management.actions.auto_allocate.enable.label" />
                )}
              </span>
            </Menu.Item>
          ) : null}
          {softAllocationsEnabled &&
            detailedAllocatedMinutes.totalAllocatedMinutes !== 0 &&
            autoAllocate === false &&
            (detailedAllocatedMinutes.softAllocatedMinutes === 0 ? (
              <Menu.ItemEdit
                onClick={() => {
                  showConvertAllocationsConfirmation(
                    detailedAllocatedMinutes.softAllocatedMinutes === 0,
                  );
                }}
                icon={<Unlocked16 />}
              >
                <span>
                  <FormattedMessage id="resource_management.actions.allocations.change_medium.soft" />
                </span>
              </Menu.ItemEdit>
            ) : (
              <Menu.ItemEdit
                onClick={() => {
                  showConvertAllocationsConfirmation(
                    detailedAllocatedMinutes.softAllocatedMinutes === 0,
                  );
                }}
                icon={<Locked16 />}
              >
                <span>
                  <FormattedMessage id="resource_management.actions.allocations.change_medium.hard" />
                </span>
              </Menu.ItemEdit>
            ))}
        </Menu>
      }
      primaryButtonsProps={[
        {
          icon: <Flag16 />,
          tooltip:
            startDate != null ? (
              <FormattedMessage id="resource_management.actions.go_to_start_of_project" />
            ) : (
              <FormattedMessage id="resource_management.actions.shift_start_date.disabled" />
            ),
          onClick: goToStartOfProject,
          disabled: startDate == null,
        },
      ]}
      onClick={onWrapperClick}
    >
      <NameCell
        logo={
          status != null ? (
            <ProjectStatusIndicator status={status} />
          ) : (
            <Avatar
              src={getThumbnailUrl(customer.companyLogoUrl)}
              name={projectName}
            />
          )
        }
        primaryText={
          <TextWrapperWithEllipsis>{projectName}</TextWrapperWithEllipsis>
        }
        secondaryText={
          <SecondaryTextWrapper>
            {status != null ? (
              <Avatar
                size={12}
                src={getThumbnailUrl(customer.companyLogoUrl)}
                name={projectName}
              />
            ) : null}
            <TextWrapperWithEllipsis>
              {customer.companyName}
            </TextWrapperWithEllipsis>
          </SecondaryTextWrapper>
        }
      />
    </CellWrapper>
  );
};
